import type { FunnelRow } from "../api";
import { count } from "../format";

/**
 * The default funnel ladder. Stage keys are the ones the schema actually
 * stores (migrations/001_init.sql), labelled the way the design brief names
 * them. Every rung is always drawn — a zero rung is the whole point of the
 * view. Callers with a saved funnel definition pass their own `ladder`.
 */
const LADDER: Array<{ key: string; label: string }> = [
  { key: "impression", label: "Impressions" },
  { key: "visit", label: "Visits" },
  { key: "engaged", label: "Engaged" },
  { key: "lead", label: "Leads" },
  { key: "qualified", label: "Qualified" },
  { key: "signup", label: "Signups" },
  { key: "activated", label: "Activated" },
  { key: "converted", label: "First payment" },
  { key: "payment", label: "Repeat" },
];

export function FunnelStages({
  rows,
  channel,
  ladder = LADDER,
}: {
  rows: FunnelRow[];
  channel: string;
  ladder?: Array<{ key: string; label: string }>;
}) {
  const totals = new Map<string, number>();
  for (const r of rows) {
    if (channel !== "All" && (r.channel ?? "unattributed") !== channel) continue;
    totals.set(r.stage, (totals.get(r.stage) ?? 0) + r.volume);
  }

  const values = ladder.map((s) => totals.get(s.key) ?? 0);
  const max = Math.max(...values, 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", marginTop: 20 }}>
      {ladder.map((s, i) => {
        const v = values[i];
        const next = values[i + 1];
        // Compressed so a 400k → 200 funnel stays readable end to end.
        const width = Math.max(Math.pow(v / max, 0.42), v > 0 ? 0.04 : 0) * 100;
        const conv = i < ladder.length - 1 && v > 0 ? (next / v) * 100 : null;
        return (
          <div key={s.key}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "132px minmax(0, 1fr) 92px",
                alignItems: "center",
                gap: 18,
                height: 40,
              }}
            >
              <div style={{ fontSize: 13.5, color: "var(--text-2)" }}>{s.label}</div>
              <div
                style={{
                  height: 10,
                  background: "var(--surface-inset)",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${width}%`,
                    borderRadius: 3,
                    background:
                      i >= ladder.length - 2 ? "var(--pos)" : i >= ladder.length / 2.5 ? "var(--text-4)" : "var(--border-hover)",
                  }}
                />
              </div>
              <div className="num" style={{ fontSize: 13.5, textAlign: "right" }}>
                {count(v)}
              </div>
            </div>
            {i < ladder.length - 1 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "132px minmax(0, 1fr) 92px",
                  gap: 18,
                  alignItems: "center",
                  height: 20,
                }}
              >
                <div />
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 1, height: 14, background: "var(--border)" }} />
                  <span
                    className="num"
                    style={{
                      fontSize: 11.5,
                      color: conv === null ? "var(--text-faint)" : conv < 15 ? "var(--warn)" : "var(--text-faint)",
                    }}
                  >
                    {conv === null ? "—" : `${conv.toFixed(1)}% →`}
                  </span>
                </div>
                <div />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
